import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import {
  useFormContext,
} from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Popper from '@mui/material/Popper';
import groupBy from 'lodash/groupBy';
import head from 'lodash/head';
import keyBy from 'lodash/keyBy';
import orderBy from 'lodash/orderBy';
import values from 'lodash/values';
import Table from 'core/components/VirtualTable';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import {
  useConfirmDialog,
} from 'core/hooks';
import AdjustmentCodeAmountCell from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/AdjustmentCodeAmountCell';
import {
  getEOBServiceAllocationRowId,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/columns';
import type {
  ClaimAdjustment,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/EOBAdjustmentCodePopover';
import {
  ServiceAllocation,
} from 'pages/Dashboard/pages/Billing/types';
import {
  useGetEOBDataForBilling,
} from 'pages/Dashboard/pages/Billing/hooks';
import Button from 'pages/Dashboard/components/Button';
import {
  EOBDataForClaimViewDTO,
  EOBServiceCodePaymentInfoViewDTO,
} from 'dtos';
import {
  paymentsEventChannel,
  EOBReviewEditAdjustmentsParams,
} from 'pages/Dashboard/services/eventChannels';
import {
  WizardContext,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/wizardContext';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import {
  AdjustmentExceptionMap,
  getPatientClaimAllocations,
  PatientClaimAllocations,
  syncWithAdjustments,
} from 'pages/Dashboard/pages/Billing/Payments/utils';
import Link from 'core/components/Link';
import Skeleton from 'core/components/Skeleton';
import {
  formatDate,
} from 'utils/date';

type DraftByKey = Record<string, ServiceAllocation>;

type ClaimMeta = {
  formattedVisitDate?: string;
  typeOfVisitDescription?: string | null;
};

function getServiceAllocationKey(item: ServiceAllocation) {
  return `${item?.claim?.claimId ?? 0}-${item?.id ?? item?.visitNoteBillingCodeId ?? 0}`;
}

function cloneDraftAllocation(item: ServiceAllocation): ServiceAllocation {
  return {
    ...item,
    adjustments: (item.adjustments ?? []).map((adjustment) => ({ ...adjustment })),
    adjustmentBaseAmounts: item.adjustmentBaseAmounts
      ? { ...item.adjustmentBaseAmounts }
      : undefined,
  };
}

function buildDraftMapsFromForm(
  paymentsInfo: EOBServiceCodePaymentInfoViewDTO[],
  patientId: number,
  adjustmentExceptionMap: AdjustmentExceptionMap,
): { draftByKey: DraftByKey; initialDraftByKey: DraftByKey } {
  const groups = getPatientClaimAllocations(
    paymentsInfo,
    patientId,
    adjustmentExceptionMap,
  );
  const draftByKey: DraftByKey = {};
  const initialDraftByKey: DraftByKey = {};

  groups.forEach(({ serviceCodes }) => {
    serviceCodes.forEach((serviceCode) => {
      const key = getServiceAllocationKey(serviceCode);
      draftByKey[key] = cloneDraftAllocation(serviceCode);
      initialDraftByKey[key] = cloneDraftAllocation(serviceCode);
    });
  });

  return { draftByKey, initialDraftByKey };
}

function groupDraftByClaim(draftByKey: DraftByKey): PatientClaimAllocations[] {
  const groups = groupBy(values(draftByKey), (item) => item?.claim?.claimId ?? 0);

  return orderBy(
    values(groups).map((serviceCodes) => ({
      claim: head(serviceCodes)?.claim ?? {},
      serviceCodes,
    })),
    ['claim.claimId'],
    ['asc'],
  );
}

function areAdjustmentsDifferent(
  current: ClaimAdjustment[] = [],
  initial: ClaimAdjustment[] = [],
) {
  if (current.length !== initial.length) {
    return true;
  }

  return current.some((adjustment, index) => {
    const original = initial[index];

    return adjustment?.amount !== original?.amount
      || adjustment?.effectiveBucket !== original?.effectiveBucket
      || adjustment?.groupCode?.code !== original?.groupCode?.code
      || adjustment?.reasonCode?.code !== original?.reasonCode?.code;
  });
}

function isAllocationDraftDirty(
  draftByKey: DraftByKey,
  initialDraftByKey: DraftByKey,
) {
  const keys = new Set([
    ...Object.keys(draftByKey),
    ...Object.keys(initialDraftByKey),
  ]);

  return Array.from(keys).some((key) => {
    const current = draftByKey[key];
    const original = initialDraftByKey[key];

    if (!current || !original) {
      return Boolean(current) !== Boolean(original);
    }

    if (current.insuranceAdjustments !== original.insuranceAdjustments) {
      return true;
    }

    if (current.autoWriteOff !== original.autoWriteOff) {
      return true;
    }

    return areAdjustmentsDifferent(
      current.adjustments ?? [],
      original.adjustments ?? [],
    );
  });
}

function buildClaimMetaByClaimId(
  billingData: EOBDataForClaimViewDTO[] = [],
): Record<number, ClaimMeta> {
  const lookup: Record<number, ClaimMeta> = {};

  billingData.forEach((visit) => {
    const claimId = visit?.claim?.claimId ?? 0;

    if (!claimId) {
      return;
    }

    lookup[claimId] = {
      formattedVisitDate: formatDate(visit?.visitDateTime ?? ''),
      typeOfVisitDescription: visit?.typeOfVisitDescription,
    };
  });

  return lookup;
}

function DiscardDraftContent() {
  return (
    <span>
      You have unsaved adjustment changes. Discard them and close?
    </span>
  );
}

const serviceNameColumn: ColumnDef<ServiceAllocation> = {
  id: 'serviceName',
  accessorKey: 'serviceName',
  columnTitle: 'Service',
  minSize: 120,
  meta: { autoSized: true },
  cell: ({ row }) => (
    <span className="font-semibold truncate">
      {row.original?.serviceName ?? '--'}
    </span>
  ),
};

const insuranceAdjustmentsColumn: ColumnDef<ServiceAllocation> = {
  id: 'insuranceAdjustments',
  accessorKey: 'insuranceAdjustments',
  columnTitle: (
    <>
      Insurance
      <br />
      Adjustments
    </>
  ),
  multiLineHeader: true,
  meta: { autoSized: true },
  minSize: 120,
  maxSize: 180,
  cell: ({ row }) => (
    <AdjustmentCodeAmountCell
      row={row}
      field="insuranceAdjustments"
    />
  ),
};

const autoWriteOffColumn: ColumnDef<ServiceAllocation> = {
  id: 'autoWriteOff',
  accessorKey: 'autoWriteOff',
  columnTitle: (
    <>
      Auto
      <br />
      Write-Off
    </>
  ),
  multiLineHeader: true,
  meta: { autoSized: true },
  minSize: 120,
  maxSize: 180,
  cell: ({ row }) => (
    <AdjustmentCodeAmountCell
      row={row}
      field="autoWriteOff"
    />
  ),
};

const popoverColumns: ColumnDef<ServiceAllocation>[] = [
  serviceNameColumn,
  insuranceAdjustmentsColumn,
  autoWriteOffColumn,
];

type ClaimSectionProps = {
  claimExternalId?: string | null;
  claimId?: number;
  formattedVisitDate?: string;
  typeOfVisitDescription?: string | null;
  serviceCodes: ServiceAllocation[];
};

function ClaimSection({
  claimExternalId,
  claimId,
  formattedVisitDate,
  typeOfVisitDescription,
  serviceCodes,
}: ClaimSectionProps) {
  const tableData = useMemo(
    () => serviceCodes.map((serviceCode) => ({
      ...serviceCode,
      serviceName: serviceCode?.visitNoteBillingCode?.code ?? undefined,
    })),
    [serviceCodes],
  );

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 px-2 pt-2">
        <span className="text-xs font-semibold text-gray-500">Claim#</span>
        <Link
          text={claimExternalId}
          href={`/billing/claims/${claimId}`}
          target="_blank"
          classes={{ label: 'font-semibold text-xs', link: '!w-[120px] truncate', root: '!w-fit' }}
        />
        {formattedVisitDate && (
          <span className="text-xs font-medium text-gray-700">
              {formattedVisitDate}
          </span>
        )}
        {typeOfVisitDescription && (
          <>
            <span className="text-xs text-gray-500">•</span>
            <span className="text-xs font-medium text-gray-700 truncate">
              {typeOfVisitDescription}
            </span>
          </>
        )}
      </div>
      <div className="w-full h-full p-2">
        <Table<ServiceAllocation>
          columns={popoverColumns}
          data={tableData}
          getRowId={getEOBServiceAllocationRowId}
          className="populate-breakdown-table eob-adjustment-popover-table"
          classes={{
            root: '!h-auto',
            body: '!m-0 !p-0',
            scrollContainer: '!p-0',
            header: 'px-2 !items-end w-full',
            outerColumn: (column) => {
              const isInsurance = column.id === 'insuranceAdjustments';
              const isAutoWriteOff = column.id === 'autoWriteOff';
              const isEmeraldColumn = isInsurance || isAutoWriteOff;

              return clsx(isEmeraldColumn ? '!items-stretch' : '!items-end', {
                'eob-emerald-header-cell !border-t !border-dashed !border-emerald-400': isEmeraldColumn,
                '!border-l !rounded-tl-lg': isInsurance,
                '!border-r !rounded-tr-lg eob-emerald-cell-right': isAutoWriteOff,
              });
            },
            column: '!text-left',
            cell: (row, cell) => {
              const isLastRow = row.index === tableData.length - 1;
              const isInsurance = cell.column.id === 'insuranceAdjustments';
              const isAutoWriteOff = cell.column.id === 'autoWriteOff';

              return clsx('!h-auto !py-0', {
                '!items-stretch': cell.column.id !== 'serviceName',
                '!bg-emerald-50 !border-dashed !border-emerald-400': isInsurance || isAutoWriteOff,
                '!border-l': isInsurance,
                '!border-r eob-emerald-cell-right': isAutoWriteOff,
                '!border-b': (isInsurance || isAutoWriteOff) && isLastRow,
                '!rounded-bl-lg': isInsurance && isLastRow,
                '!rounded-br-lg': isAutoWriteOff && isLastRow,
              });
            },
            outerRow: '!w-full',
            windowsClassName: '!w-full',
            scrollbarGutter: 'inherit',
            row: 'items-stretch',
          }}
        />
      </div>
    </div>
  );
}

function ClaimSectionSkeletonRow() {
  return (
    <div className="flex items-stretch gap-2 px-2 py-1">
      <Skeleton variant="rectangular" className="!h-4 !w-1/3 rounded" />
      <Skeleton variant="rectangular" className="!h-4 !w-1/3 rounded" />
      <Skeleton variant="rectangular" className="!h-4 !w-1/3 rounded" />
    </div>
  );
}

function ClaimSectionSkeleton() {
  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 px-2 pt-2">
        <Skeleton variant="rectangular" className="!h-3 !w-12 rounded" />
        <Skeleton variant="rectangular" className="!h-3 !w-20 rounded" />
        <Skeleton variant="rectangular" className="!h-3 !w-16 rounded" />
        <Skeleton variant="rectangular" className="!h-3 !w-24 rounded" />
      </div>
      <div className="w-full p-2 flex flex-col gap-1">
        <ClaimSectionSkeletonRow />
        <ClaimSectionSkeletonRow />
      </div>
    </div>
  );
}

type Props = {
  params: EOBReviewEditAdjustmentsParams;
  onClose: () => void;
};

export default function PatientAdjustmentEditPopover({ params, onClose }: Props) {
  const {
    adjustmentExceptionMap,
    dateRange,
    patients,
  } = useContext(WizardContext);
  const { getValues, setValue } = useFormContext();
  const { confirm, closeConfirm } = useConfirmDialog();
  const [activeParams, setActiveParams] = useState(params);
  const [draftByKey, setDraftByKey] = useState<DraftByKey>({});
  const [initialDraftByKey, setInitialDraftByKey] = useState<DraftByKey>({});
  const adjustmentExceptionMapRef = useRef(adjustmentExceptionMap);
  const draftByKeyRef = useRef(draftByKey);
  const initialDraftByKeyRef = useRef(initialDraftByKey);

  adjustmentExceptionMapRef.current = adjustmentExceptionMap;
  draftByKeyRef.current = draftByKey;
  initialDraftByKeyRef.current = initialDraftByKey;

  const { anchorEl: popperElement, patientId } = activeParams;

  const {
    data: billingData = [],
    isFetching: fetchingBillingData,
  } = useGetEOBDataForBilling(patientId, dateRange);

  const claimMetaByClaimId = useMemo(
    () => buildClaimMetaByClaimId(billingData),
    [billingData],
  );

  const claimGroups = useMemo(
    () => groupDraftByClaim(draftByKey),
    [draftByKey],
  );

  const isDirty = useMemo(
    () => isAllocationDraftDirty(draftByKey, initialDraftByKey),
    [draftByKey, initialDraftByKey],
  );

  const patientsById = useMemo(() => keyBy(patients, 'patientId'), [patients]);
  const patientName = getDisplayName(patientsById[patientId]?.formattedNames);

  const resetDraftState = useCallback(() => {
    setDraftByKey({});
    setInitialDraftByKey({});
  }, []);

  const initializeDraftForPatient = useCallback((targetPatientId: number) => {
    const paymentsInfo = getValues('eob.eobServiceCodePaymentInfos') ?? [];
    const { draftByKey: nextDraft, initialDraftByKey: nextInitial } = buildDraftMapsFromForm(
      paymentsInfo,
      targetPatientId,
      adjustmentExceptionMapRef.current,
    );
    setDraftByKey(nextDraft);
    setInitialDraftByKey(nextInitial);
  }, [getValues]);

  const closePopover = useCallback(() => {
    resetDraftState();
    onClose();
  }, [onClose, resetDraftState]);

  const requestClose = useCallback(() => {
    if (!isAllocationDraftDirty(draftByKeyRef.current, initialDraftByKeyRef.current)) {
      closePopover();
      return;
    }

    confirm({
      title: 'Discard unsaved changes?',
      content: <DiscardDraftContent />,
      confirmText: 'Discard',
      onConfirm: () => {
        closeConfirm();
        closePopover();
      },
    });
  }, [closeConfirm, closePopover, confirm]);

  const applyDraftToForm = useCallback(() => {
    const info = getValues('eob.eobServiceCodePaymentInfos') ?? [];
    const currentDraft = draftByKeyRef.current;

    setValue(
      'eob.eobServiceCodePaymentInfos',
      info.map((item: ServiceAllocation) => {
        const key = getServiceAllocationKey(item);

        return item?.claim?.patientId === patientId && currentDraft[key]
          ? currentDraft[key]
          : item;
      }),
      { shouldDirty: true },
    );
    closePopover();
  }, [closePopover, getValues, setValue, patientId]);

  useEffect(() => {
    const isSwitchingPatient = activeParams.patientId !== params.patientId;

    if (
      isSwitchingPatient
      && isAllocationDraftDirty(draftByKeyRef.current, initialDraftByKeyRef.current)
    ) {
      confirm({
        title: 'Discard unsaved changes?',
        content: <DiscardDraftContent />,
        confirmText: 'Discard',
        onConfirm: () => {
          closeConfirm();
          setActiveParams(params);
        },
      });
      return;
    }

    setActiveParams(params);
  }, [params, activeParams.patientId, confirm, closeConfirm]);

  useEffect(() => {
    if (!patientId) {
      return;
    }

    initializeDraftForPatient(patientId);
  }, [patientId, initializeDraftForPatient]);

  useEffect(() => {
    const unsubscribeFromAdjustments = paymentsEventChannel.on(
      'onChangeEOBAdjustmentCode',
      ({ adjustments, data }) => {
        const key = getServiceAllocationKey(data);

        setDraftByKey((previous) => {
          const current = previous[key];

          if (!current) {
            return previous;
          }

          return {
            ...previous,
            [key]: syncWithAdjustments({
              ...current,
              deductible: null,
              coinsurance: null,
              copay: null,
              adjustments,
            }, 'adjustments', adjustmentExceptionMapRef.current),
          };
        });
      },
    );

    return unsubscribeFromAdjustments;
  }, []);

  useEffect(() => {
    let scrollContainer: HTMLElement | null = null;

    const checkVisibility = () => {
      if (!document?.contains(popperElement)) {
        onClose();
      }
    };

    scrollContainer = popperElement.closest('.populate-virtual-list-scroll')
      ?? popperElement.closest('.breakdown-scroll-container');
    scrollContainer?.addEventListener('scroll', checkVisibility);

    return () => {
      scrollContainer?.removeEventListener('scroll', checkVisibility);
    };
  }, [popperElement, onClose]);

  return (
    <Popper
      className="z-[1300] bg-white border border-gray-200 rounded shadow-lg w-[720px]"
      open
      placement="bottom-start"
      anchorEl={popperElement}
    >
      <div className="flex w-full max-h-[min(480px,70vh)] flex-col overflow-hidden">
        <div className="flex items-center justify-between bg-primary px-2 text-white">
          <h3 className="truncate text-xs font-semibold">
            {patientName ? `Edit Adjustments for ${patientName}` : 'Edit Adjustments'}
          </h3>
          <IconButton onClick={requestClose} size="small">
            <CloseIcon className="!h-5 !w-5 !text-white" />
          </IconButton>
        </div>
        <div className="flex w-full flex-1 flex-col overflow-y-auto p-1 gap-2">
          {fetchingBillingData && (
            <ClaimSectionSkeleton />
          )}
          {!fetchingBillingData && claimGroups.length === 0 && (
            <p className="px-2 py-4 text-center text-sm text-gray-500">Nothing to edit</p>
          )}
          {!fetchingBillingData && claimGroups.map(({ claim, serviceCodes }) => {
            const meta = claimMetaByClaimId[claim?.claimId ?? 0];

            return (
              <ClaimSection
                key={claim?.claimId ?? 0}
                claimExternalId={claim?.externalId}
                claimId={claim?.claimId}
                formattedVisitDate={meta?.formattedVisitDate}
                typeOfVisitDescription={meta?.typeOfVisitDescription}
                serviceCodes={serviceCodes}
              />
            );
          })}
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-200 px-3 py-2">
          <Button
            variant="outlined"
            type="button"
            onClick={requestClose}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="button"
            disabled={!isDirty}
            onClick={applyDraftToForm}
          >
            Apply Changes
          </Button>
        </div>
      </div>
    </Popper>
  );
}
